import { Injectable } from '@angular/core';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState
} from '@microsoft/signalr';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { ConfigurationService } from '../system-configuration';
import { Logger } from '../system-services';
import { AuthenticationService } from '.';

/**
 * PI's SignalR Service
 */
@Injectable({ providedIn: 'root' })
export class SignalRService {
    private listeners: Map<
        string,
        PISignalRMessageEventListener<PISignalRMessageData>
    >;
    private connection: HubConnection;
    private connectionPromise?: Promise<void>;

    constructor(
        private readonly authenticationService: AuthenticationService,
        private readonly configurationService: ConfigurationService,
        private readonly logger: Logger
    ) {
        this.listeners = new Map<
            string,
            PISignalRMessageEventListener<PISignalRMessageData>
        >();
    }

    /**
     * Connects to the SignalR service
     *
     * @returns a Promise that will resolve when the connection is complete
     */
    async connect(): Promise<void> {
        // If the connection is finsihed, resolve the promsie right away
        if (
            this.connection &&
            this.connection.state === HubConnectionState.Connected
        ) {
            return Promise.resolve();
            // Otherwise, return the connection promise so all connect reqeusts
            // await the same one.
        } else if (this.connectionPromise) {
            return this.connectionPromise;
        }

        this.connectionPromise = new Promise((resolve) => {
            this.connection = new HubConnectionBuilder()
                .withUrl(this.configurationService.getSignalRUrl(), {
                    accessTokenFactory: async () =>
                        firstValueFrom(this.authenticationService.getToken())
                })
                .withAutomaticReconnect()
                .build();

            this.connection
                .start()
                .then(() => {
                    // If ListenFor was called before the connection was completed, then we need to wire up any registered values.
                    this.listeners.forEach((_, eventTarget) => {
                        this.connection.on(eventTarget, (data) =>
                            this.eventTriggerHandler(eventTarget, data)
                        );
                    });
                    resolve();
                })
                .catch((err) => {
                    this.logger.logError('SignalrConnectionFailure', err);
                    resolve();
                });
        });

        return this.connectionPromise;
    }

    /**
     * Adds the current user to the provided group for updates on that group
     * when SignalR gets a message for them.
     *
     * By default this is restricted to the current company of the user. If a
     * companyGuid is provided, the user will be added to the group for that
     * company instead as long as they have permissions to do so. Permissions
     * check happens on the server side.
     */
    async addToGroup(
        groupName: GroupName,
        companyGuid?: string
    ): Promise<void> {
        if (
            this.connection &&
            this.connection.state === HubConnectionState.Connected
        ) {
            const joinRequest: PISignalRJoinGroupRequestCto = {
                groupName,
                companyGuid
            };
            await this.connection.invoke('JoinGroupV2', joinRequest);
        } else {
            return Promise.reject(
                "Connection to SignalR wasn't established yet"
            );
        }
    }

    /**
     * Adds an subscription to events for a given PISignalRMessage type.
     *
     * @param type Message Data Object to listen for events coming from SignalR
     * @returns An Observable of events that target the message target name
     */
    listenFor<T extends PISignalRMessage<K>, K extends PISignalRMessageData>(
        type: new () => T
    ): Observable<K> {
        const signalrMessageTemp = new type();

        if (this.listeners.has(signalrMessageTemp.eventTarget)) {
            return this.listeners.get(signalrMessageTemp.eventTarget)
                .observable as Observable<K>;
        }

        const newlistener = new PISignalRMessageEventListener<K>();
        this.listeners.set(signalrMessageTemp.eventTarget, newlistener);

        // If we're already connected then register this listener to be triggered by events from SignalR
        if (
            this.connection &&
            this.connection.state === HubConnectionState.Connected
        ) {
            this.connection.on(signalrMessageTemp.eventTarget, (data) =>
                this.eventTriggerHandler<K>(
                    signalrMessageTemp.eventTarget,
                    data
                )
            );
        }

        return newlistener.observable;
    }

    private eventTriggerHandler<T extends PISignalRMessageData>(
        eventName: string,
        data: T
    ) {
        if (this.listeners.has(eventName)) {
            this.listeners.get(eventName).subject.next(data);
        }
    }
}

/**
 * Base interface for all SignalR message types. Contains the event target string to be used to listen for signalr messages
 */
export interface PISignalRMessage<T extends PISignalRMessageData> {
    data: T;
    eventTarget: string;
}

/**
 * Base interface for all SignalR messages
 */
export interface PISignalRMessageData {}

/**
 * Interface for the SignalR message that is sent when a user wants to join
 * a group. This should match the associated CTO in
 * {@link https://github.com/predictiveindex/pi-signalr-functions pi-signalr-functions}.
 */
export interface PISignalRJoinGroupRequestCto {
    groupName: string;
    companyGuid?: string;
}

/**
 * The pre-defined enum of group names. Each group name will be appended
 * with the current user's company GUID automatically in the pi-signalr-functions
 * service. For example `DiagnoseOrgUploadGroup_c3b23e7d-6771-410c-84ab-d30e98cd84e7`;
 */
export enum GroupName {
    'DiagnoseOrgUploadGroup' = 'DiagnoseOrgUploadGroup',
    'InspireOrgMemberUploadGroup' = 'InspireOrgMemberUploadGroup',
    'PlatformOrgUploadGroup' = 'PlatformOrgUploadGroup',
    'DiagnoseAISummaryGroup' = 'DiagnoseAISummaryGroup'
}

class PISignalRMessageEventListener<T extends PISignalRMessageData> {
    subject: Subject<T>;
    observable: Observable<T>;

    constructor() {
        this.subject = new Subject<T>();
        this.observable = this.subject.asObservable();
    }
}
